export const ASSET_RETRY_QUERY_PARAM = "asset-retry";

const ASSET_RETRY_STORAGE_KEY = "clienthub:asset-retry-at";
const ASSET_RETRY_COOLDOWN_MS = 60_000;

const ASSET_LOAD_ERROR_PATTERNS = [
  /Loading chunk [^\s]+ failed/i,
  /Loading CSS chunk [^\s]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
];

export interface AssetRecoveryTarget {
  location: {
    href: string;
    replace(url: string): void;
  };
  sessionStorage?: Pick<Storage, "getItem" | "setItem">;
}

export interface AssetRecoveryCleanupTarget {
  location: { href: string };
  history: Pick<History, "replaceState" | "state">;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const candidate = error as {
      message?: unknown;
      reason?: { message?: unknown } | unknown;
    };

    if (typeof candidate.message === "string") {
      return candidate.message;
    }

    if (
      candidate.reason &&
      typeof candidate.reason === "object" &&
      typeof (candidate.reason as { message?: unknown }).message === "string"
    ) {
      return (candidate.reason as { message: string }).message;
    }
  }

  return "";
}

export function isAssetLoadError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return ASSET_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function recoverFromAssetLoadError(
  error: unknown,
  target: AssetRecoveryTarget = window,
  now: number = Date.now(),
): boolean {
  if (!isAssetLoadError(error)) {
    return false;
  }

  const reloadUrl = new URL(target.location.href);
  const queryRetryAt = Number(
    reloadUrl.searchParams.get(ASSET_RETRY_QUERY_PARAM) ?? 0,
  );

  let storageRetryAt = 0;
  try {
    storageRetryAt = Number(
      target.sessionStorage?.getItem(ASSET_RETRY_STORAGE_KEY) ?? 0,
    );
  } catch {
    // The query parameter still provides a reload-loop guard when storage is blocked.
  }

  if (now - Math.max(queryRetryAt, storageRetryAt) < ASSET_RETRY_COOLDOWN_MS) {
    return false;
  }

  try {
    target.sessionStorage?.setItem(ASSET_RETRY_STORAGE_KEY, String(now));
  } catch {
    // Continue with the query-parameter guard when storage is blocked.
  }

  reloadUrl.searchParams.set(ASSET_RETRY_QUERY_PARAM, String(now));
  target.location.replace(reloadUrl.toString());
  return true;
}

export function clearAssetRecoveryQueryParam(
  target: AssetRecoveryCleanupTarget = window,
): void {
  const currentUrl = new URL(target.location.href);
  if (!currentUrl.searchParams.has(ASSET_RETRY_QUERY_PARAM)) {
    return;
  }

  currentUrl.searchParams.delete(ASSET_RETRY_QUERY_PARAM);
  target.history.replaceState(target.history.state, "", currentUrl.toString());
}
